// THE RACES WE DIDN'T BET — the Strategy page's non-slate detail + CSV export.
//
// THE ASK. "The strategy page needs the non-slate detail: top 4 horses, rated prices, model outputs,
// trainer, jockey, barrier, overlay — and a CSV download."
//
// WHY IT IS MORE THAN A TABLE. The page shows the BETS: a filtered view of the model's opinion that
// displays the races where value was found and hides every race where it was not. You cannot judge a
// selective engine from its selections alone. "3 bets from 26 races" is either excellent discipline
// or a broken gate, and the SELECTIONS look identical in both cases. The 23 races we passed are the
// evidence that tells them apart.
import React, { useCallback, useEffect, useState } from 'react';
import {
  getRaceDetail, downloadRaceDetailCsv, RaceDetailView, DetailRace, DetailRunner, RaceDetailFieldGroup,
} from '../api/strategy';

// "add toggles for what data to pull in the report" (Paul, 2026-09-10) — one checkbox per group,
// matching raceDetail.js's FIELD_GROUPS on the server exactly (server is the source of truth; this
// list is just its display labels — see field_groups on the response if these ever drift).
const FIELD_GROUP_LABELS: Record<RaceDetailFieldGroup, string> = {
  core: 'Core ranks & outputs',
  profile: 'Barrier / weight / jockey / trainer',
  form: 'Full form & margins',
  market: 'Market at decision',
  price_snapshots: 'Price snapshots (early→late)',
  result: 'Result & settled BSP',
};
const ALL_FIELD_GROUPS = Object.keys(FIELD_GROUP_LABELS) as RaceDetailFieldGroup[];

const price = (n: number | null) => (n == null ? '—' : `$${n.toFixed(2)}`);
const pct = (n: number | null) => (n == null ? '—' : `${n.toFixed(1)}%`);

// An overlay is a claim that the market is paying more than the runner is worth. It is the number
// the entire Value thesis rests on, so it is never rendered bare: the sign is explicit and the
// colour is not the only carrier of meaning.
const Overlay: React.FC<{ v: number | null }> = ({ v }) => {
  if (v == null) return <span style={{ color: 'var(--tab-text-faint)' }}>—</span>;
  const p = v * 100;
  return (
    <span style={{ color: p > 0 ? 'var(--tab-success)' : p < 0 ? 'var(--tab-danger)' : 'var(--tab-text)', fontWeight: p > 0 ? 600 : 400 }}>
      {p > 0 ? '+' : ''}{p.toFixed(1)}%
    </span>
  );
};

// Which on-screen columns each toggle controls — deliberately the SAME grouping as raceDetail.js's
// FIELD_GROUPS on the server, so "turn off Form" means the same thing here as it does in the CSV.
// `recent_runs`'s full per-run history is export-only (a table cell is the wrong shape for a nested
// array of past runs) — the compact `form` string is what represents the "form" group on screen.
type Fields = Set<RaceDetailFieldGroup>;
const on = (fields: Fields, g: RaceDetailFieldGroup) => fields.has(g);

const RunnerRow: React.FC<{ r: DetailRunner; fields: Fields }> = ({ r, fields }) => (
  <tr style={{ background: r.bet ? 'var(--tab-success-soft)' : undefined }}>
    <td className="sr-num">{r.model_rank}</td>
    <td>
      <span style={{ fontWeight: r.bet ? 700 : 500 }}>{r.runner}</span>
      {r.bet && <span title="This runner is in today's slate" className="rdt-badge rdt-badge-bet">BET</span>}
      {/* A vetoed runner scored but was refused. Silently omitting the flag would make the model
          look as though it simply rated the horse poorly. */}
      {r.vetoed && <span title={r.veto_rule || 'believability veto'} className="rdt-badge rdt-badge-veto">VETO</span>}
    </td>
    {on(fields, 'profile') && <>
      <td className="sr-num">{r.number ?? '—'}</td>
      <td className="sr-num">{r.barrier ?? '—'}</td>
      <td className="sr-num">{r.weight || '—'}</td>
      <td style={{ fontSize: 'var(--fs-sm)' }}>{r.jockey || '—'}</td>
      <td style={{ fontSize: 'var(--fs-sm)' }}>{r.trainer || '—'}</td>
    </>}
    {on(fields, 'form') && <td className="sr-num" title="Compact form — full per-run history (margins, class, prior prices) is in the CSV export">{r.form || '—'}</td>}
    {on(fields, 'core') && <>
      <td className="sr-num">{pct(r.win_pct)}</td>
      {/* Both model views, side by side. Showing only the calibrated price would hide the size of
          the correction; showing only the raw one would misrepresent what the engine decided on. */}
      <td className="sr-num" style={{ color: 'var(--tab-text-faint)' }} title="The raw model, before calibration">{pct(r.raw_win_pct)}</td>
      <td className="sr-num" style={{ fontWeight: 600 }}>{price(r.rated_price)}</td>
      <td className="sr-num"><Overlay v={r.overlay} /></td>
      <td className="sr-num">{r.reliability == null ? '—' : `${r.reliability}%`}</td>
    </>}
    {on(fields, 'market') && <td className="sr-num">{price(r.market_price)}</td>}
    {on(fields, 'price_snapshots') && <>
      <td className="sr-num" title={r.earliest_snapshot_minutes_to_jump != null ? `${r.earliest_snapshot_minutes_to_jump}min to jump` : ''}>{price(r.earliest_snapshot_price)}</td>
      <td className="sr-num" title={r.latest_snapshot_minutes_to_jump != null ? `${r.latest_snapshot_minutes_to_jump}min to jump` : ''}>{price(r.latest_snapshot_price)}</td>
      <td className="sr-num">{r.price_drift_pct == null ? '—' : `${r.price_drift_pct > 0 ? '+' : ''}${r.price_drift_pct.toFixed(1)}%`}</td>
    </>}
    {on(fields, 'result') && <>
      <td className="sr-num">{r.result || '—'}</td>
      <td className="sr-num" title="Settled Betfair BSP">{price(r.starting_price)}</td>
    </>}
  </tr>
);

const RaceBlock: React.FC<{ race: DetailRace; open: boolean; onToggle: () => void; fields: Fields }> = ({ race, open, onToggle, fields }) => (
  <div className="rdt-card">
    <div onClick={onToggle} className={`rdt-card-header${race.in_slate ? ' in-slate' : ''}`}>
      <span style={{ fontWeight: 700 }}>{open ? '▾' : '▸'} {race.date} · {race.track} R{race.race}</span>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--tab-text-muted)' }}>
        {race.runners_scored} scored · field {race.field_size}
      </span>
      {race.in_slate
        ? <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--tab-success)', fontWeight: 600 }}>BET: {race.bets.join(', ')}</span>
        : <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--tab-text-muted)', flex: 1 }}>{race.no_bet_reason}</span>}
      {/* The biggest overlay in the race, bet or not. On a passed race this is the whole question:
          did we stand down on nothing, or walk past something we should have taken? */}
      <span style={{ fontSize: 'var(--fs-sm)', marginLeft: 'auto' }} title="Best overlay available in this race, bet or not">
        best <Overlay v={race.best_overlay} />
      </span>
    </div>
    {open && (
      <div style={{ overflowX: 'auto', padding: '0 var(--sp-2) var(--sp-2)' }}>
        <table className="sr-table" style={{ fontSize: 'var(--fs-sm)', width: '100%' }}>
          <thead><tr>
            <th className="sr-num" title="The model's running order, by calibrated probability">#</th>
            <th>Runner</th>
            {on(fields, 'profile') && <>
              <th className="sr-num">No.</th><th className="sr-num">Bar</th><th className="sr-num">Wgt</th>
              <th>Jockey</th><th>Trainer</th>
            </>}
            {on(fields, 'form') && <th className="sr-num">Form</th>}
            {on(fields, 'core') && <>
              <th className="sr-num" title="Calibrated win probability — what the engine decided on">Win%</th>
              <th className="sr-num" title="The raw model, before calibration">Raw%</th>
              <th className="sr-num" title="Calibrated fair price">Rated</th>
              <th className="sr-num" title="Market ÷ rated − 1. Positive = the market pays more than we rate it.">Overlay</th>
              <th className="sr-num">Rel.</th>
            </>}
            {on(fields, 'market') && <th className="sr-num">Market</th>}
            {on(fields, 'price_snapshots') && <>
              <th className="sr-num" title="Earliest recorded price before the jump">Early</th>
              <th className="sr-num" title="Latest recorded price before the jump">Late</th>
              <th className="sr-num" title="% change from earliest to latest — positive drifted, negative shortened">Drift</th>
            </>}
            {on(fields, 'result') && <><th className="sr-num">Result</th><th className="sr-num">BSP</th></>}
          </tr></thead>
          <tbody>{race.runners.map((r) => <RunnerRow key={r.runner} r={r} fields={fields} />)}</tbody>
        </table>
        {race.runners.length < race.runners_scored && (
          // Never let a truncated view read as the whole field.
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--tab-text-faint)', marginTop: 'var(--sp-1)' }}>
            Showing top {race.runners.length} of {race.runners_scored} scored. Switch to “All runners”, or export the CSV, for the full field.
          </div>
        )}
      </div>
    )}
  </div>
);

const today = () => new Date().toISOString().slice(0, 10);

const RaceDetailTable: React.FC<{ date?: string }> = ({ date: dateProp }) => {
  const [date, setDate] = useState(dateProp || today());
  // "update to a date range" (Paul, 2026-09-10) — a single day is still the default (and the fastest
  // query), the range is opt-in via this toggle rather than replacing the single-date view outright.
  const [rangeMode, setRangeMode] = useState(false);
  const [from, setFrom] = useState(dateProp || today());
  const [to, setTo] = useState(dateProp || today());
  const [view, setView] = useState<RaceDetailView | null>(null);
  const [slate, setSlate] = useState<'all' | 'on' | 'off'>('all');
  const [top, setTop] = useState<number | 'all'>(4);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // "add toggles for what data to pull in the report" (Paul, 2026-09-10) — every group on by
  // default, matching his own framing ("give me everything"), pared down from here.
  const [fields, setFields] = useState<Fields>(() => new Set(ALL_FIELD_GROUPS));
  const toggleField = (g: RaceDetailFieldGroup) => setFields((f) => {
    const next = new Set(f);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  useEffect(() => { if (dateProp) { setDate(dateProp); setFrom(dateProp); setTo(dateProp); } }, [dateProp]);

  const query = useCallback(() => ({
    ...(rangeMode ? { from, to } : { date }),
    top, slate: slate === 'all' ? undefined : slate,
    fields: fields.size < ALL_FIELD_GROUPS.length ? Array.from(fields) : undefined,
  }), [rangeMode, from, to, date, top, slate, fields]);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try { setView(await getRaceDetail(query())); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setView(null); }
    finally { setBusy(false); }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setErr(null);
    try { await downloadRaceDetailCsv(query()); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <section className="sr-section" style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Every race we scored</h2>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--tab-text-muted)' }}>including the ones we didn’t bet — the engine’s full opinion</span>
      </div>

      <div className="sr-controls">
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={rangeMode} onChange={(e) => setRangeMode(e.target.checked)} />
          Date range
        </label>
        {rangeMode ? (
          <>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="From" />
            <span style={{ color: 'var(--tab-text-muted)' }}>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} title="To" />
          </>
        ) : (
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        )}
        <select value={slate} onChange={(e) => setSlate(e.target.value as 'all' | 'on' | 'off')}>
          <option value="all">All races</option>
          <option value="off">Races we passed</option>
          <option value="on">Races we bet</option>
        </select>
        <select value={String(top)} onChange={(e) => setTop(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
          <option value="4">Top 4 runners</option>
          <option value="8">Top 8 runners</option>
          <option value="all">All runners</option>
        </select>
        <button onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
        <button className="ghost" onClick={exportCsv} title="Full field, every race — not the top-N shown here">Download CSV</button>
        {view && (
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--tab-text-muted)' }}>
            {view.races_in_slate} of {view.races_scored} races in the slate
          </span>
        )}
      </div>

      {/* Data toggles — same grouping the CSV columns use, so what you see is what you'll export. */}
      <div className="rdt-toggle-row">
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--tab-text-muted)', fontWeight: 600 }}>Data:</span>
        {ALL_FIELD_GROUPS.map((g) => (
          <label key={g} className="rdt-toggle">
            <input type="checkbox" checked={fields.has(g)} onChange={() => toggleField(g)} />
            {FIELD_GROUP_LABELS[g]}
          </label>
        ))}
      </div>

      {err && <div className="sr-error">{err}</div>}

      {/* NOT decoration. If no map is fitted, the rated price IS the raw model price and every
          overlay on this page is the raw model's — which is not the overlay the portfolio thesis
          assumes. An uncalibrated page that looks calibrated is worse than no page. */}
      {view && (
        <div className={`rdt-banner ${view.calibration.ready ? 'rdt-banner-ok' : 'rdt-banner-warn'}`}>
          {view.calibration.note}
        </div>
      )}

      {/* 2026-09-11: a wide date range with Price snapshots on can be too large to join live safely
          — never fail silently or hang, say exactly why and what to do about it. */}
      {view && view.price_snapshots_skipped && (
        <div className="rdt-banner rdt-banner-warn">{view.price_snapshots_skipped.reason}</div>
      )}
      {view && view.top_clamped && (
        <div className="rdt-banner rdt-banner-warn">{view.top_clamped.reason}</div>
      )}
      {view && view.races_capped && (
        <div className="rdt-banner rdt-banner-warn">{view.races_capped.reason}</div>
      )}

      {view && view.races.length === 0 && (
        <p className="sr-empty">No scored runners recorded for {rangeMode ? `${from} to ${to}` : date}.</p>
      )}

      {view && view.races.map((r) => (
        <RaceBlock key={r.race_id} race={r} open={!!open[r.race_id]} fields={fields}
          onToggle={() => setOpen((o) => ({ ...o, [r.race_id]: !o[r.race_id] }))} />
      ))}
    </section>
  );
};

export default RaceDetailTable;
